import { useEffect, useRef } from 'react';

/**
 * Adds `.visible` to an element once it scrolls into view, which is what the
 * `.fade-in` CSS animates against.
 *
 * Two things this has to get right, because the class is what makes the content
 * *visible at all* — if it never lands, the section stays blank:
 *
 *   • Someone who asked for reduced motion gets `.visible` immediately. The
 *     animation is skipped, the content is not.
 *   • If IntersectionObserver is missing, everything is revealed up front
 *     rather than left hidden forever.
 *
 * `once: false` keeps re-observing, but the default is to stop after the first
 * reveal — there is nothing to re-trigger, and a live observer per section adds
 * up on a page with a few dozen of them.
 */
const useFadeIn = (threshold = 0.5) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reveal = () => el.classList.add('visible');

    // Respect the OS-level "reduce motion" setting, and degrade safely if the
    // browser has no IntersectionObserver.
    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      reveal();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        reveal();
        // Nothing re-hides, so stop watching once it has fired.
        observer.disconnect();
      },
      { threshold }
    );

    observer.observe(el);

    /* Anything already on screen at mount never triggers a fresh intersection
       in some browsers, so reveal it up front rather than waiting for a scroll
       that may never come (the previous version did this too, but read layout
       synchronously during the effect — this defers it to the next frame so it
       cannot force a reflow mid-commit). */
    const frame = requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        reveal();
        observer.disconnect();
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [threshold]);

  return ref;
};

export default useFadeIn;
